import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import ProfileForm from "@/components/profile/ProfileForm";
import { Loader2, UserCircle2, LogOut, Users, Menu, ChevronLeft } from "lucide-react";
import { fetchUserProfile } from "@/utils/userProfileUtils";
import { sessionManager } from "@/utils/sessionManager";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useIdleLogoutWithWarning } from "@/hooks/useIdleLogoutWithWarning";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { FeedbackButton } from "@/components/FeedbackButton";

const ProfileEditor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);
  const [userData, setUserData] = useState({
    id: "",
    email: "",
    name: "",
    firstName: "",
    lastName: "",
    role: "",
    profileImageUrl: "",
    connectionKey: "",
    websiteUrl: "",
    bookingCalendar: "",
    showActivity: "no",
    country: "",
    language: "",
    languageSecondary: "",
    profileBio: ""
  });

  // Only show idle warning for therapists
  const isTherapist = userData.role === 'therapist';
  const { showWarning, countdown } = useIdleLogoutWithWarning({
    logout: async () => {
      await supabase.auth.signOut();
      localStorage.removeItem('userRole');
      localStorage.removeItem('userId');
      navigate('/auth');
    },
    idleDuration: 570000,
    warningDuration: 30000,
  });

  useEffect(() => {
    let isMounted = true;
    
    async function loadUserData() {
      try {
        if (!isMounted) return;
        setLoading(true);
        setLoadingError(false);
        
        // First verify the user session is valid
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          console.log("No active session - redirecting to auth");
          if (isMounted) {
            navigate('/auth');
            return;
          }
        }
        
        // Then verify the user role
        const hasValidRole = await sessionManager.verifyUserRole(navigate);
        if (!hasValidRole) {
          console.log("Invalid or missing role detected - redirecting");
          if (isMounted) {
            setLoading(false);
            setLoadingError(true);
          }
          return;
        }
        
        // Then load the profile data using the fetchUserProfile function from userProfileUtils
        const profileData = await fetchUserProfile(navigate);
        
        if (profileData && isMounted) {
          setUserData(profileData);
        } else if (isMounted) {
          console.error("Failed to load profile data");
          setLoadingError(true);
          toast.error("Failed to load your profile data");
        }
      } catch (error) {
        if (!isMounted) return;
        console.error("Error loading user data:", error);
        setLoadingError(true);
        toast.error("Failed to load your profile data");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }
    
    loadUserData();
    
    // Clean up function - important for preventing state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const getInitials = () => {
    if (userData.firstName && userData.lastName) {
      return `${userData.firstName[0]}${userData.lastName[0]}`.toUpperCase();
    } else if (userData.name) {
      return userData.name.substring(0, 2).toUpperCase();
    }
    return "U";
  };

  // Handle retry if loading failed
  const handleRetryLoading = () => {
    setLoading(true);
    setLoadingError(false);
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-[#3BAFDA]" />
      </div>
    );
  }

  if (loadingError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#E8F0F8] to-white flex items-center justify-center p-4">
        <Card className="max-w-md mx-auto shadow-lg backdrop-blur-sm border border-red-200 bg-white">
          <CardHeader className="bg-gradient-to-r from-red-50 to-white rounded-t-lg">
            <CardTitle className="text-xl text-red-600">Profile Loading Error</CardTitle>
            <CardDescription>We encountered an issue loading your profile</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <p className="mb-4">This could be due to a network issue or your session may have expired.</p>
            <div className="flex justify-between">
              <button 
                onClick={() => navigate('/auth')} 
                className="px-4 py-2 bg-[#E8F0F8] hover:bg-[#E8F0F8]/80 rounded-md transition-colors"
              >
                Go Home
              </button>
              <button 
                onClick={handleRetryLoading} 
                className="px-4 py-2 bg-[#3BAFDA] hover:bg-[#333B44] text-white rounded-md transition-colors"
              >
                Try Again
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full">
      {isTherapist && (
        <Dialog open={showWarning}>
          <DialogContent className="bg-gradient-to-br from-yellow-50 to-white border-yellow-200 shadow-2xl">
            <div className="flex flex-col items-center text-center space-y-4">
              <AlertTriangle className="h-12 w-12 text-yellow-500 mb-2 animate-bounce" />
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-yellow-700">Are you still there?</DialogTitle>
                <DialogDescription className="text-base text-yellow-900">
                  For your security, you will be logged out in
                  <span className="mx-2 px-3 py-1 rounded-lg bg-yellow-100 text-yellow-700 font-extrabold text-xl border border-yellow-300 animate-pulse">
                    {countdown}
                  </span>
                  seconds due to inactivity.<br />
                  <span className="text-sm text-yellow-700">Click or press any key to stay logged in.</span>
                </DialogDescription>
              </DialogHeader>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {/* Full-screen blue gradient background */}
      <div className="fixed inset-0 w-full h-full bg-gradient-to-b from-[#E8F0F8] to-white z-0" />
      <div className="relative z-10 min-h-screen w-full">
        <img src="https://app.clamalo.com/images/1.png" alt="Clou Background" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-10 w-[60vw] max-w-[600px] pointer-events-none select-none z-0" />
        <div className="container mx-auto px-4 py-8 relative z-10">
          <Card className="shadow-lg backdrop-blur-sm border border-[#CCCCCC] bg-white">
            <CardHeader className="bg-gradient-to-r from-[#E8F0F8] to-white rounded-t-lg">
              <div className="flex items-center space-x-3 justify-between">
                <div className="flex items-center space-x-3">
                  <UserCircle2 className="h-8 w-8 text-[#3BAFDA]" />
                  <div>
                    <CardTitle className="text-2xl bg-clip-text text-transparent bg-gradient-to-r from-[#333B44] to-[#3BAFDA]">
                      My Profile
                    </CardTitle>
                    <CardDescription>Update your personal information</CardDescription>
                  </div>
                </div>
                {/* Back button top right */}
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    if (userData.role === 'therapist') {
                      navigate('/therapist');
                    } else if (userData.role === 'client') {
                      navigate('/journal');
                    } else {
                      navigate('/auth');
                    }
                  }}
                  className="border-[#CCCCCC] hover:bg-[#E8F0F8]"
                >
                  <ChevronLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <ProfileForm 
                userData={userData} 
                userInitials={getInitials()} 
              />
            </CardContent>
            <CardFooter className="bg-[#E8F0F8]/50 rounded-b-lg border-t border-[#CCCCCC] px-6 py-4">
              <p className="text-sm text-muted-foreground">Your profile information helps us maintain correct information.</p>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditor;
